/**
 * useRequestBadgeCounts — Returns per-menu "unaddressed request" counts for the
 * sidebar badges and the app-wide banner.
 *
 * Shape: { tickets, ticketPool, serverRequests, vpnRequests, softwareRequests,
 *          hardwareRequests, reminders, contractAnalysis, total }
 *
 * Counts are role-scoped by the backend (what needs the CURRENT user's action).
 * Polls every 45 seconds and refreshes immediately on relevant socket
 * notifications, so a badge appears the moment a request comes in and clears
 * once it is addressed.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../api/axios';
import socket from '../socket';
import { useAuth } from '../context/AuthContext';

const EMPTY = {
  tickets: 0,
  ticketPool: 0,
  serverRequests: 0,
  vpnRequests: 0,
  softwareRequests: 0,
  hardwareRequests: 0,
  reminders: 0,
  contractAnalysis: 0,
  total: 0,
};

// Notification types that should trigger an immediate re-count.
const REFRESH_TRIGGERS = [
  'ticket_',
  'server_request',
  'vpn_request',
  'software_request',
  'hardware_request',
  'service_renewal',
  'renewal_',
  'contract_analysis',
  'request_',
];

export default function useRequestBadgeCounts() {
  const { user } = useAuth();
  const [counts, setCounts] = useState(EMPTY);
  const intervalRef = useRef(null);

  const fetchCounts = useCallback(async () => {
    if (!user) { setCounts(EMPTY); return; }
    try {
      const { data } = await api.get('/notifications/badge-counts');
      setCounts({ ...EMPTY, ...data });
    } catch {
      // silent — never break the sidebar / banner if this fails
    }
  }, [user]);

  useEffect(() => {
    fetchCounts();

    // Poll periodically as a safety net for anything socket misses.
    intervalRef.current = setInterval(fetchCounts, 45_000);

    // Refresh immediately when a relevant notification arrives.
    const onNotification = (notif) => {
      const type = String(notif?.type || '');
      if (REFRESH_TRIGGERS.some((prefix) => type.includes(prefix))) {
        fetchCounts();
      } else {
        // Unknown type — refresh anyway; a badge count is cheap and always fresh.
        fetchCounts();
      }
    };
    socket.on('notification', onNotification);

    return () => {
      clearInterval(intervalRef.current);
      socket.off('notification', onNotification);
    };
  }, [fetchCounts]);

  return counts;
}
